import { Button, Card, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useInvestigationsServiceGetListSpecimensEndpoint } from 'src/api/queries'
import { SpecimenItemDto } from 'src/api/requests'
import { Paragraph } from 'src/components'
import { TestStatusTag } from 'src/features/tests/components'
import { dayMonthYearHourMinutesFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'

import { InvestigationTestModal } from '../../PersonInvestigations'

type Props = {
  investigationDemandsIds?: string[]
}

export const DetailsLaboratoryCard = ({ investigationDemandsIds }: Props) => {
  const { t, currentLang } = useLocales()

  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string>()

  const { data: specimens, isLoading: isSpecimensLoading } =
    useInvestigationsServiceGetListSpecimensEndpoint(
      { demandIds: investigationDemandsIds },
      [investigationDemandsIds],
      { enabled: (investigationDemandsIds?.length ?? 0) > 0 }
    )

  const onCloseTestsModal = () => {
    setSelectedSpecimenId(undefined)
  }

  const columns: ColumnsType<SpecimenItemDto> = [
    {
      key: 'investigation number',
      title: t('investigations.investigation_number'),
      dataIndex: 'code'
    },
    {
      key: 'created date',
      title: t('investigations.created_date'),
      render: ({ createdAt }) =>
        dayjs.utc(createdAt).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'harvest date',
      title: t('investigations.harvest_date'),
      render: ({ harvestDate }) =>
        harvestDate
          ? dayjs
              .utc(harvestDate)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'specimen type',
      title: t('consultations.specimen_type'),
      render: (_, { type }) => type?.translation?.[currentLang.value] ?? '-'
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => <TestStatusTag status={status} />
    },
    {
      key: 'test type',
      title: t('tests_results.type'),
      render: (_, { demand }) => {
        const text = (
          <div>
            {demand?.categories
              ? demand?.categories?.map((item) => (
                  <div key={item.id}>
                    {item.translation?.[currentLang.value]}
                  </div>
                ))
              : '-'}
          </div>
        )

        return (
          <Paragraph
            ellipsis={{
              rows: 2,
              tooltip: text
            }}
          >
            {text}
          </Paragraph>
        )
      }
    },
    {
      key: 'test_results',
      title: t('investigations.tests'),
      render: (_, { id }) => (
        <Button
          type="link"
          onClick={() => {
            setSelectedSpecimenId(id)
          }}
        >
          {t('common.details')}
        </Button>
      )
    }
  ]

  const nonTbSpecimens =
    specimens?.data?.filter((specimen) => !specimen.isTb) ?? []
  const tbSpecimens =
    specimens?.data?.filter((specimen) => !!specimen.isTb) ?? []

  return (
    <>
      {!!specimens?.data?.length && (
        <Card>
          <Flex vertical gap={24}>
            {!!nonTbSpecimens.length && (
              <Card title={t('organizational_structure.lab')}>
                <Table
                  rowKey="id"
                  columns={columns}
                  loading={isSpecimensLoading}
                  scroll={{ x: 'max-content' }}
                  dataSource={nonTbSpecimens}
                  pagination={false}
                />
              </Card>
            )}

            {!!tbSpecimens.length && (
              <Card title={t('consultations.tb_investigations')}>
                <Table
                  rowKey="id"
                  columns={columns}
                  loading={isSpecimensLoading}
                  scroll={{ x: 'max-content' }}
                  dataSource={tbSpecimens}
                  pagination={false}
                />
              </Card>
            )}
          </Flex>
        </Card>
      )}

      {selectedSpecimenId && (
        <InvestigationTestModal
          specimenIds={[selectedSpecimenId]}
          onCancel={onCloseTestsModal}
        />
      )}
    </>
  )
}
